import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../../components/button/Button";
import ImageUploader from "../../../../components/inputs/imageUploader/ImageUploader";
import TextInput from "../../../../components/inputs/textInput/TextInput";
import SelectInput from "../../../../components/inputs/selectInput/SelectInput";
import CheckBox from "../../../../components/inputs/checkBox/CheckBox";
import Loader from "../../../../components/loader/Loader";
import SettingsPagesContainer from "../../../../components/settingsPagesContainer/SettingsPagesContainer";
import TextWrapper from "../../../../components/textWrapper/TextWrapper";
import {
  createSquadBanner,
  clearFieldErrors,
} from "../../../../features/squadBanners/squadBannersSlice";
import styles from "./squadBanners.module.css";

const DEFAULT_CURRENCY = "USD";
const RARITY_OPTIONS = [
  { value: "common", label: "عادي" },
  { value: "rare", label: "نادر" },
  { value: "epic", label: "ملحمي" },
  { value: "legendary", label: "أسطوري" },
];
const AVAILABLE_FOR_OPTIONS = [
  { value: "user", label: "مستخدم" },
  { value: "squad", label: "سكواد" },
];
const UNLOCK_TYPE_OPTIONS = [
  { value: "free", label: "مجاني" },
  { value: "purchase", label: "شراء" },
];

const ImagePreviewCard = ({ title, file, previewUrl }) => {
  if (!file || !previewUrl) return null;

  return (
    <div className={styles.previewCard}>
      <div className={styles.previewHeader}>
        <TextWrapper textStyle="textMid">{title}</TextWrapper>
        <span className={styles.previewFileName}>{file.name}</span>
      </div>
      <div className={styles.previewImageWrapper}>
        <img
          src={previewUrl}
          alt={`معاينة ${title}`}
          className={styles.previewImage}
        />
      </div>
    </div>
  );
};

const SquadBanners = () => {
  const dispatch = useDispatch();
  const { createStatus } = useSelector((state) => state.squadBanners);

  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [nameAr, setNameAr] = useState("");
  const [availableFor, setAvailableFor] = useState("");
  const [unlockType, setUnlockType] = useState("");
  const [isPaid, setIsPaid] = useState(false);
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [isActive, setIsActive] = useState(true);
  const [isDefaultForSquad, setIsDefaultForSquad] = useState(false);
  const [isDefaultForUser, setIsDefaultForUser] = useState(false);
  const [sortOrder, setSortOrder] = useState("0");
  const [rarity, setRarity] = useState("common");
  const [season, setSeason] = useState("");
  const [headerFile, setHeaderFile] = useState(null);
  const [backgroundFile, setBackgroundFile] = useState(null);
  const [headerPreviewUrl, setHeaderPreviewUrl] = useState("");
  const [backgroundPreviewUrl, setBackgroundPreviewUrl] = useState("");

  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (!headerFile) {
      setHeaderPreviewUrl("");
      return undefined;
    }

    const nextPreviewUrl = URL.createObjectURL(headerFile);
    setHeaderPreviewUrl(nextPreviewUrl);

    return () => {
      URL.revokeObjectURL(nextPreviewUrl);
    };
  }, [headerFile]);

  useEffect(() => {
    if (!backgroundFile) {
      setBackgroundPreviewUrl("");
      return undefined;
    }

    const nextPreviewUrl = URL.createObjectURL(backgroundFile);
    setBackgroundPreviewUrl(nextPreviewUrl);

    return () => {
      URL.revokeObjectURL(nextPreviewUrl);
    };
  }, [backgroundFile]);

  const validate = () => {
    const errors = {};
    if (!key?.trim()) errors.key = "مطلوب";
    if (!name?.trim()) errors.name = "مطلوب";
    if (!nameAr?.trim()) errors.nameAr = "مطلوب";
    if (!availableFor) errors.availableFor = "مطلوب";
    if (!unlockType) errors.unlockType = "مطلوب";
    if (!headerFile) errors.header = "مطلوب صورة الهيدر";
    if (!backgroundFile) errors.background = "مطلوب صورة الخلفية";
    if (isPaid && (price === "" || Number(price) < 0)) {
      errors.price = "السعر مطلوب ويجب أن يكون أكبر من أو يساوي صفر";
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    dispatch(clearFieldErrors());
    if (!validate()) return;

    const formData = new FormData();
    formData.append("type", "banner");
    formData.append("key", key.trim().toLowerCase());
    formData.append("name", name.trim());
    formData.append("nameAr", nameAr.trim());
    formData.append("availableFor", availableFor);
    formData.append("unlockType", unlockType);
    formData.append("isPaid", isPaid);
    formData.append("price", isPaid ? String(price) : "0");
    formData.append("currency", currency || DEFAULT_CURRENCY);
    formData.append("isActive", isActive);
    formData.append("isDefaultForSquad", isDefaultForSquad);
    formData.append("isDefaultForUser", isDefaultForUser);
    formData.append("sortOrder", sortOrder !== "" ? String(sortOrder) : "0");
    formData.append("rarity", rarity);
    if (season?.trim()) formData.append("season", season.trim());
    formData.append("header", headerFile);
    formData.append("background", backgroundFile);

    dispatch(createSquadBanner(formData));
  };

  if (createStatus === "loading") {
    return (
      <div className={styles.loaderContainer}>
        <Loader />
      </div>
    );
  }

  return (
    <SettingsPagesContainer
      title="إنشاء بانر سكواد"
      fullWidth
      fullHeight
      displayContainerClassName={styles.bannerDisplay}
    >
      <div className={styles.pageLayout}>
        <div className={styles.formSection}>
          <div className={styles.sectionHeading}>
            <TextWrapper textStyle="textMid">تفاصيل البانر</TextWrapper>
          </div>
          <div className={styles.inputWrapper}>
            <TextWrapper textStyle="textMid">المفتاح (فريد)</TextWrapper>
            <TextInput
              height="50px"
              width="100%"
              borderRadius="12px"
              placeHolder="مثال: default-squad-banner"
              bgColor="grey"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              error={fieldErrors.key}
              bordered
            />
          </div>
          <div className={styles.inputWrapper}>
            <TextWrapper textStyle="textMid">الاسم</TextWrapper>
            <TextInput
              height="50px"
              width="100%"
              borderRadius="12px"
              placeHolder="اسم البانر"
              bgColor="grey"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={fieldErrors.name}
              bordered
            />
          </div>
          <div className={styles.inputWrapper}>
            <TextWrapper textStyle="textMid">الاسم بالعربية</TextWrapper>
            <TextInput
              height="50px"
              width="100%"
              borderRadius="12px"
              placeHolder="اسم البانر بالعربية"
              bgColor="grey"
              value={nameAr}
              onChange={(e) => setNameAr(e.target.value)}
              error={fieldErrors.nameAr}
              bordered
            />
          </div>
          <div className={styles.inputWrapper}>
            <TextWrapper textStyle="textMid">متاح لـ</TextWrapper>
            <SelectInput
              value={availableFor}
              setValue={setAvailableFor}
              placeHolder="اختر"
              className={styles.selectField}
            >
              {AVAILABLE_FOR_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </SelectInput>
            {fieldErrors.availableFor && (
              <span className={styles.errorText}>
                {fieldErrors.availableFor}
              </span>
            )}
          </div>
          <div className={styles.inputWrapper}>
            <TextWrapper textStyle="textMid">نوع الفتح</TextWrapper>
            <SelectInput
              value={unlockType}
              setValue={setUnlockType}
              placeHolder="اختر"
              className={styles.selectField}
            >
              {UNLOCK_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </SelectInput>
            {fieldErrors.unlockType && (
              <span className={styles.errorText}>{fieldErrors.unlockType}</span>
            )}
          </div>
          <div className={styles.checkboxGrid}>
            <CheckBox
              id="banner-isPaid"
              label="مدفوع"
              checked={isPaid}
              onChange={(e) => setIsPaid(e.target.checked)}
              className={styles.checkboxControl}
              labelClass={styles.checkboxLabel}
            />
            <CheckBox
              id="banner-isActive"
              label="نشط"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className={styles.checkboxControl}
              labelClass={styles.checkboxLabel}
            />
            <CheckBox
              id="banner-isDefaultForSquad"
              label="افتراضي للسكواد"
              checked={isDefaultForSquad}
              onChange={(e) => setIsDefaultForSquad(e.target.checked)}
              className={styles.checkboxControl}
              labelClass={styles.checkboxLabel}
            />
            <CheckBox
              id="banner-isDefaultForUser"
              label="افتراضي للمستخدم"
              checked={isDefaultForUser}
              onChange={(e) => setIsDefaultForUser(e.target.checked)}
              className={styles.checkboxControl}
              labelClass={styles.checkboxLabel}
            />
          </div>
          {isPaid && (
            <div className={styles.inlineFields}>
              <div className={styles.inputWrapper}>
                <TextWrapper textStyle="textMid">السعر</TextWrapper>
                <TextInput
                  height="50px"
                  width="100%"
                  borderRadius="12px"
                  placeHolder="0"
                  bgColor="grey"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  error={fieldErrors.price}
                  bordered
                />
              </div>
              <div className={styles.inputWrapper}>
                <TextWrapper textStyle="textMid">العملة</TextWrapper>
                <TextInput
                  height="50px"
                  width="100%"
                  borderRadius="12px"
                  placeHolder="USD"
                  bgColor="grey"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  bordered
                />
              </div>
            </div>
          )}
          <div className={styles.inlineFields}>
            <div className={styles.inputWrapper}>
              <TextWrapper textStyle="textMid">الندرة</TextWrapper>
              <SelectInput
                value={rarity}
                setValue={setRarity}
                placeHolder="اختر"
                noPlaceHolder
                className={styles.selectField}
              >
                {RARITY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </SelectInput>
            </div>
            <div className={styles.inputWrapper}>
              <TextWrapper textStyle="textMid">ترتيب العرض</TextWrapper>
              <TextInput
                height="50px"
                width="100%"
                borderRadius="12px"
                placeHolder="0"
                bgColor="grey"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                bordered
              />
            </div>
          </div>
          <div className={styles.inputWrapper}>
            <TextWrapper textStyle="textMid">الموسم (اختياري)</TextWrapper>
            <TextInput
              height="50px"
              width="100%"
              borderRadius="12px"
              placeHolder="موسم 2024"
              bgColor="grey"
              value={season}
              onChange={(e) => setSeason(e.target.value)}
              bordered
            />
          </div>
          <div className={styles.submitWrapper}>
            <Button
              size="bigSize"
              variant="blackVariant"
              functionallity={handleSubmit}
            >
              <TextWrapper textStyle="textMid">إنشاء البانر</TextWrapper>
            </Button>
          </div>
        </div>

        <div className={styles.imagesSection}>
          <div className={styles.sectionHeading}>
            <TextWrapper textStyle="textMid">صور البانر</TextWrapper>
          </div>
          <div className={styles.inputWrapper}>
            <TextWrapper textStyle="textMid">هيدر</TextWrapper>
            <ImageUploader
              inputId="banner-header-upload"
              label="رفع صورة الهيدر"
              onFileSelect={setHeaderFile}
              error={fieldErrors.header}
            />
            <ImagePreviewCard
              title="معاينة الهيدر"
              file={headerFile}
              previewUrl={headerPreviewUrl}
            />
          </div>
          <div className={styles.inputWrapper}>
            <TextWrapper textStyle="textMid">خلفية</TextWrapper>
            <ImageUploader
              inputId="banner-background-upload"
              label="رفع صورة الخلفية"
              onFileSelect={setBackgroundFile}
              error={fieldErrors.background}
            />
            <ImagePreviewCard
              title="معاينة الخلفية"
              file={backgroundFile}
              previewUrl={backgroundPreviewUrl}
            />
          </div>
        </div>
      </div>
    </SettingsPagesContainer>
  );
};

export default SquadBanners;
